import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  FileText,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Search,
  Filter,
  MessageSquare,
  X,
  Check,
  ChevronDown,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { portalGet, portalPost } from '@/lib/portal-api';

interface PortalSubmission {
  id: string;
  candidateFirstName: string;
  candidateLastName: string;
  jobTitle: string;
  jobId: string;
  status: string;
  submittedAt: string;
  feedback: {
    decision: 'thumbs_up' | 'thumbs_down' | 'maybe' | null;
    comments: string | null;
  } | null;
}

interface PortalJobOption {
  id: string;
  title: string;
}

async function fetchPortalSubmissions(params: {
  search?: string;
  jobId?: string;
  status?: string;
}): Promise<PortalSubmission[]> {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set('search', params.search);
  if (params.jobId) searchParams.set('jobId', params.jobId);
  if (params.status) searchParams.set('status', params.status);
  return portalGet<PortalSubmission[]>(`/submissions?${searchParams.toString()}`);
}

async function fetchPortalJobOptions(): Promise<PortalJobOption[]> {
  return portalGet<PortalJobOption[]>('/jobs?fields=id,title');
}

async function submitFeedback(payload: {
  submissionId: string;
  decision: 'thumbs_up' | 'thumbs_down' | 'maybe';
  comments?: string;
}): Promise<void> {
  await portalPost(`/submissions/${payload.submissionId}/feedback`, {
    decision: payload.decision,
    comments: payload.comments || null,
  });
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    shortlisted: 'Shortlisted',
    interview: 'Interview',
    offered: 'Offered',
    hired: 'Hired',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  };
  return labels[status] || status;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'submitted':
      return 'text-blue-400 bg-blue-500/10';
    case 'under_review':
      return 'text-yellow-400 bg-yellow-500/10';
    case 'shortlisted':
      return 'text-emerald-400 bg-emerald-500/10';
    case 'interview':
      return 'text-purple-400 bg-purple-500/10';
    case 'offered':
      return 'text-cyan-400 bg-cyan-500/10';
    case 'hired':
      return 'text-green-400 bg-green-500/10';
    case 'rejected':
      return 'text-red-400 bg-red-500/10';
    case 'withdrawn':
      return 'text-gray-400 bg-gray-500/10';
    default:
      return 'text-text-secondary bg-white/5';
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function redactLastName(lastName: string): string {
  if (!lastName) return '';
  return lastName.charAt(0).toUpperCase() + '.';
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview', label: 'Interview' },
  { value: 'offered', label: 'Offered' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

// Feedback inline component
function FeedbackActions({
  submission,
}: {
  submission: PortalSubmission;
}) {
  const queryClient = useQueryClient();
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(submission.feedback?.comments || '');
  const [pendingRating, setPendingRating] = useState<'thumbs_up' | 'thumbs_down' | 'maybe' | null>(null);

  const mutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'submissions'] });
      setShowComment(false);
      setPendingRating(null);
    },
  });

  const currentRating = submission.feedback?.decision ?? null;

  const handleRating = (rating: 'thumbs_up' | 'thumbs_down' | 'maybe') => {
    setPendingRating(rating);
    setShowComment(true);
  };

  const handleSubmitFeedback = () => {
    if (!pendingRating) return;
    mutation.mutate({
      submissionId: submission.id,
      decision: pendingRating,
      comments: comment.trim() || undefined,
    });
  };

  const handleCancelComment = () => {
    setShowComment(false);
    setPendingRating(null);
    setComment(submission.feedback?.comments || '');
  };

  return (
    <div className="space-y-3">
      {/* Rating buttons */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-tertiary mr-1">Feedback:</span>
        <button
          onClick={() => handleRating('thumbs_up')}
          disabled={mutation.isPending}
          className={`p-1.5 rounded-lg transition-colors ${
            currentRating === 'thumbs_up'
              ? 'bg-green-500/20 text-green-400'
              : 'text-text-tertiary hover:text-green-400 hover:bg-green-500/10'
          }`}
          title="Interested"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleRating('maybe')}
          disabled={mutation.isPending}
          className={`p-1.5 rounded-lg transition-colors ${
            currentRating === 'maybe'
              ? 'bg-yellow-500/20 text-yellow-400'
              : 'text-text-tertiary hover:text-yellow-400 hover:bg-yellow-500/10'
          }`}
          title="Maybe"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleRating('thumbs_down')}
          disabled={mutation.isPending}
          className={`p-1.5 rounded-lg transition-colors ${
            currentRating === 'thumbs_down'
              ? 'bg-red-500/20 text-red-400'
              : 'text-text-tertiary hover:text-red-400 hover:bg-red-500/10'
          }`}
          title="Not interested"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>

        {submission.feedback?.comments && !showComment && (
          <button
            onClick={() => {
              setPendingRating(currentRating);
              setShowComment(true);
            }}
            className="p-1.5 rounded-lg text-text-tertiary hover:text-text-secondary hover:bg-white/5 transition-colors"
            title="View comment"
          >
            <MessageSquare className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Comment input */}
      {showComment && (
        <div className="flex items-start gap-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add an optional comment..."
            rows={2}
            className="flex-1 rounded-md border border-white/20 bg-bg-elevated px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
          />
          <div className="flex flex-col gap-1">
            <button
              onClick={handleSubmitFeedback}
              disabled={mutation.isPending}
              className="p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              title="Submit feedback"
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={handleCancelComment}
              disabled={mutation.isPending}
              className="p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-white/10 transition-colors"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {mutation.isError && (
        <p className="text-xs text-red-400">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed to submit feedback'}
        </p>
      )}
    </div>
  );
}

export function PortalSubmissionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);

  const jobIdFromUrl = searchParams.get('jobId') || '';
  const [selectedJobId, setSelectedJobId] = useState(jobIdFromUrl);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const handleJobChange = (jobId: string) => {
    setSelectedJobId(jobId);
    setJobDropdownOpen(false);
    if (jobId) {
      setSearchParams({ jobId });
    } else {
      setSearchParams({});
    }
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setStatusDropdownOpen(false);
  };

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      jobId: selectedJobId || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    }),
    [debouncedSearch, selectedJobId, statusFilter],
  );

  const { data: submissions, isLoading } = useQuery<PortalSubmission[]>({
    queryKey: ['portal', 'submissions', queryParams],
    queryFn: () => fetchPortalSubmissions(queryParams),
    refetchInterval: 60000,
  });

  const { data: jobOptions } = useQuery<PortalJobOption[]>({
    queryKey: ['portal', 'jobOptions'],
    queryFn: fetchPortalJobOptions,
  });

  const selectedJobLabel = useMemo(() => {
    if (!selectedJobId) return 'All Jobs';
    return jobOptions?.find((j) => j.id === selectedJobId)?.title || 'All Jobs';
  }, [selectedJobId, jobOptions]);

  const selectedStatusLabel = useMemo(() => {
    return STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || 'All Statuses';
  }, [statusFilter]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Candidate Submissions</h1>
        <p className="text-text-secondary mt-1">
          Review candidates submitted for your open positions
        </p>
      </div>

      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
            <Input
              placeholder="Search candidates..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-text-tertiary" />

            {/* Job filter dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setJobDropdownOpen(!jobDropdownOpen);
                  setStatusDropdownOpen(false);
                }}
                className="flex items-center gap-2 h-10 px-3 rounded-md border border-white/20 bg-bg-elevated text-sm text-text-primary hover:bg-white/5 transition-colors min-w-[140px]"
              >
                <span className="truncate">{selectedJobLabel}</span>
                <ChevronDown className={`h-4 w-4 text-text-tertiary ml-auto transition-transform ${jobDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {jobDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setJobDropdownOpen(false)} />
                  <div className="absolute top-full mt-1 left-0 w-56 max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-bg-elevated shadow-lg z-50">
                    <button
                      onClick={() => handleJobChange('')}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        !selectedJobId
                          ? 'bg-emerald-600/20 text-emerald-400'
                          : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                      }`}
                    >
                      All Jobs
                    </button>
                    {jobOptions?.map((job) => (
                      <button
                        key={job.id}
                        onClick={() => handleJobChange(job.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors truncate ${
                          selectedJobId === job.id
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                        }`}
                      >
                        {job.title}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Status filter dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setStatusDropdownOpen(!statusDropdownOpen);
                  setJobDropdownOpen(false);
                }}
                className="flex items-center gap-2 h-10 px-3 rounded-md border border-white/20 bg-bg-elevated text-sm text-text-primary hover:bg-white/5 transition-colors min-w-[140px]"
              >
                <span className="truncate">{selectedStatusLabel}</span>
                <ChevronDown className={`h-4 w-4 text-text-tertiary ml-auto transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {statusDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setStatusDropdownOpen(false)} />
                  <div className="absolute top-full mt-1 left-0 w-48 max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-bg-elevated shadow-lg z-50">
                    {STATUS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          statusFilter === option.value
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Submissions List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-text-tertiary" />
        </div>
      ) : !submissions || submissions.length === 0 ? (
        <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen py-16 text-center">
          <FileText className="h-12 w-12 text-text-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary">No submissions found</h3>
          <p className="text-sm text-text-tertiary mt-2">
            {debouncedSearch || selectedJobId || statusFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Candidate submissions will appear here once your recruiter shares them.'}
          </p>
          {(debouncedSearch || selectedJobId || statusFilter !== 'all') && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
                setSelectedJobId('');
                setStatusFilter('all');
                setSearchParams({});
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen overflow-hidden">
          {/* Table Header */}
          <div className="hidden lg:grid lg:grid-cols-[1fr_1fr_120px_120px_200px] gap-4 px-5 py-3 border-b border-white/10 text-xs font-medium text-text-tertiary uppercase tracking-wider">
            <span>Candidate</span>
            <span>Job</span>
            <span>Status</span>
            <span>Date</span>
            <span>Feedback</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-white/5">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="px-5 py-4 hover:bg-white/[0.02] transition-colors"
              >
                {/* Desktop row */}
                <div className="hidden lg:grid lg:grid-cols-[1fr_1fr_120px_120px_200px] gap-4 items-start">
                  {/* Candidate */}
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {submission.candidateFirstName} {redactLastName(submission.candidateLastName)}
                    </p>
                  </div>

                  {/* Job */}
                  <div>
                    <p className="text-sm text-text-secondary truncate">{submission.jobTitle}</p>
                  </div>

                  {/* Status */}
                  <div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}
                    >
                      {getStatusLabel(submission.status)}
                    </span>
                  </div>

                  {/* Date */}
                  <div>
                    <span className="text-sm text-text-tertiary">
                      {formatDate(submission.submittedAt)}
                    </span>
                  </div>

                  {/* Feedback */}
                  <div>
                    <FeedbackActions submission={submission} />
                  </div>
                </div>

                {/* Mobile card */}
                <div className="lg:hidden space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary">
                        {submission.candidateFirstName} {redactLastName(submission.candidateLastName)}
                      </p>
                      <p className="text-xs text-text-tertiary mt-0.5 truncate">
                        {submission.jobTitle}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(submission.status)}`}
                    >
                      {getStatusLabel(submission.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-tertiary">
                      {formatDate(submission.submittedAt)}
                    </span>
                  </div>
                  <FeedbackActions submission={submission} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
